import GraphemeSplitter from 'grapheme-splitter';
import _ from 'lodash';

// Cached instance of Intl.Segmenter which provides locale-sensitive text segmentation
const segmenter: Intl.Segmenter | undefined =
    typeof Intl !== 'undefined' && 'Segmenter' in Intl
        ? new Intl.Segmenter(undefined, { granularity: 'grapheme' }) // grapheme granularity for emoji support
        : undefined;
const graphemeSplitter = new GraphemeSplitter();

// Emoji-safe extraction
const getFirstChar = (str: string): string => {
    if (!str) return '';
    // Use cached Intl.Segmenter if available, otherwise use GraphemeSplitter
    if (segmenter) {
        return Array.from(segmenter.segment(str))[0]?.segment || '';
    }
    return graphemeSplitter.splitGraphemes(str)[0] || '';
};

const getFirstTwoChars = (str: string): string => {
    if (!str) return '';
    if (segmenter) {
        let result = '';
        let count = 0;
        for (const segment of segmenter.segment(str)) {
            result += segment.segment;
            count++;
            if (count >= 2) break;
        }
        return result;
    }
    return graphemeSplitter.splitGraphemes(str).slice(0, 2).join('');
};

export const getPlayerDisplayInitials = ({
    firstName,
    lastName,
    email,
    playerAttributes,
    number,
}: {
    firstName?: string;
    lastName?: string;
    email?: string;
    playerAttributes?: { jerseyNumber?: string };
    number?: string;
}) => {
    if (firstName && lastName) {
        return getFirstChar(firstName) + getFirstChar(lastName);
    }
    if (firstName) {
        return getFirstTwoChars(firstName);
    }
    if (lastName) {
        return getFirstTwoChars(lastName);
    }
    if (email) {
        return getFirstTwoChars(email);
    }
    if (playerAttributes?.jerseyNumber) {
        return getFirstTwoChars(playerAttributes.jerseyNumber);
    }
    if (number) {
        return getFirstTwoChars(number);
    }

    return '--';
};

export const buildPlayerDisplayName = (first_name: string, last_name: string, number?: string, email?: string) => {
    const jerseyNumber = formatPlayerNumber(number);

    let name = formatFirstNameLastName('normal', first_name, last_name);
    if (name === '') name = email || '';

    return _.compact([name, jerseyNumber]).join(', ');
};

export const formatPlayerNumber = (number?: string): string => {
    return number ? `#${number}` : '';
};

export function formatFirstNameLastName(variant: 'short' | 'normal', firstName?: string, lastName?: string) {
    let formattedName = '';
    if (!firstName) {
        formattedName = lastName || '';
    } else if (!lastName) {
        formattedName = firstName || '';
    } else {
        const formattedFirstName = variant === 'short' ? firstName[0] : firstName;
        formattedName = `${formattedFirstName} ${lastName}`;
    }

    return formattedName.trim();
}

export const buildShortPlayerDisplayName = (firstName: string, lastName: string, number?: string, email?: string) => {
    const formattedName = formatFirstNameLastName('short', firstName, lastName);
    if (formattedName !== '') {
        return formattedName;
    }

    if (number) {
        return `#${number}`;
    }

    return email || '';
};
